<template>
    <div
        :class="{
            'sticky-node': hasChildren,
            'bg-background': !background,
            'tree-node-with-branch': level === 0 || !isLast,
            'tree-node-with-branch-last': level !== 0 && isLast
        }"
        :style="{ background, width: customWidth }"
        class="relative overflow-visible">
        <!-- Vertical branch line -->
        <div class="branch-vertical" />

        <!-- Node header -->
        <div class="node-header flex items-center gap-1.5 py-1 px-2" :style="{ zIndex: zIndexHeader }">
            <div class="branch-horizontal"></div>

            <Checkbox
                :checked="isNodeChecked"
                :indeterminate="areChildrenPartiallySelected"
                :disabled="node.disabled || (!editMode && !disableCreateAndEdit)"
                :invalid="isError"
                @update:modelValue="toggleSelected" />

            <ChevronDown
                v-if="(hasChildren || node.isGroupNode) && !collapsed"
                class="h-4 w-4 cursor-pointer"
                tabindex="0"
                role="button"
                :aria-label="`Collapse ${node.name}`"
                @click="toggleCollapse(node.id)"
                @keydown.enter.prevent="toggleCollapse(node.id)"
                @keydown.space.prevent="toggleCollapse(node.id)" />
            <ChevronRight
                v-else-if="(hasChildren || node.isGroupNode) && collapsed"
                class="h-4 w-4 cursor-pointer"
                tabindex="0"
                role="button"
                :aria-label="`Expand ${node.name}`"
                @click="toggleCollapse(node.id)"
                @keydown.enter.prevent="toggleCollapse(node.id)"
                @keydown.space.prevent="toggleCollapse(node.id)" />

            <div class="flex-1 items-center ml-2">
                <div
                    v-if="!isEditing || !editMode || disableCreateAndEdit || node.isGroupNode"
                    :class="[
                        node.disabled ? 'opacity-50' : '',
                        isError ? 'text-destructive' : 'text-foreground',
                        node.isGroupNode ? 'font-medium text-muted-foreground' : '',
                        'max-w-[25rem] overflow-hidden text-ellipsis whitespace-nowrap'
                    ]">
                    {{ node.name }}
                </div>
                <Input
                    v-else
                    v-model="node.name"
                    v-sanitize
                    v-sanitize-csv
                    @keydown.enter.prevent="finishEdit"
                    @input="debouncedDuplicate"
                    :aria-invalid="isError"
                    :class="'w-full node-input'"
                    placeholder="* Name - cannot be null, duplicated or contain * (wildcard)" />
            </div>

            <Button
                v-if="editMode && !disableCreateAndEdit && !node.isGroupNode"
                variant="ghost"
                size="sm"
                @click="removeNode(parent, node)"
                class="h-5 w-5 p-0 text-destructive hover:text-destructive">
                <X class="h-3 w-3" />
            </Button>
            <Button
                v-if="editMode && !disableCreateAndEdit && !node.isGroupNode"
                variant="ghost"
                size="sm"
                @click="isEditing ? finishEdit() : startEdit()"
                class="h-5 px-1 text-xs">
                {{ isEditing ? 'Done' : 'Edit' }}
            </Button>
        </div>

        <!-- Group Inputs Section (for virtual group nodes) -->
        <div v-if="node.isGroupNode && inputs.group && inputs.group.length > 0" class="group-inputs-toolbox">
            <div class="pl-15 pr-2 pt-2 pb-1 space-y-1">
                <TreeInputRenderer
                    v-for="input in inputs.group"
                    :key="input.key"
                    :config="input"
                    :model-value="getInputValue('group', node.groupKey || node.id, input.key)"
                    :instance-id="`group-${node.groupKey || node.id}-${input.key}`"
                    @change="handleInputChange('group', input.key, $event, node.groupKey || node.id)" />
            </div>
        </div>

        <!-- Toolbar for group nodes (virtual groups) - just the separator line -->
        <div
            v-if="node.isGroupNode && (!collapsed || !node.children?.length)"
            class="children-toolbar-separator"
            :class="{ 'bg-background': !background }"
            :style="{ background, zIndex: zIndexToolbar }"></div>

        <!-- Toolbar for regular parent nodes -->
        <div
            v-if="nextLevel && (!collapsed || !node.children?.length) && !node.isGroupNode"
            class="children-toolbar"
            :class="{ 'bg-background': !background }"
            :style="{ background, zIndex: zIndexToolbar }">
            <div class="text-sm font-medium text-muted-foreground">
                {{ filteredChildCount !== null ? filteredChildCount : node.children?.length || 0 }}
                {{ (nextLevel?.schemaLevel || 'item').toLowerCase()
                }}{{ (filteredChildCount !== null ? filteredChildCount : node.children?.length || 0) !== 1 ? 's' : '' }}
            </div>

            <Button v-if="editMode && !disableCreateAndEdit" variant="outline" size="sm" @click.prevent="addChild" class="h-6 px-1.5 text-xs">
                <Plus class="h-3 w-3 mr-1" />
                Add {{ nextLevel?.schemaLevel || 'Item' }}
            </Button>

            <Button v-if="editMode && !disableCreateAndEdit" variant="outline" size="sm" @click.stop="openFileUpload" class="h-6 px-1.5 text-xs">
                <Upload class="h-3 w-3 mr-1" />
                Import {{ nextLevel?.schemaLevel || 'Item' }} .CSV
            </Button>
            <input ref="fileInput" type="file" accept=".csv" class="hidden" @change="onCsvChange" />
        </div>
    </div>
</template>
<script>
import { Plus, ChevronRight, ChevronDown, X, Upload } from 'lucide-vue-next';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { debounce } from 'lodash';
import TreeInputRenderer from './TreeInputRenderer.vue';

export default {
    name: 'TreeNode',
    components: {
        Checkbox,
        Button,
        Input,
        Plus,
        ChevronRight,
        ChevronDown,
        X,
        Upload,
        TreeInputRenderer
    },
    props: {
        node: { type: Object, required: true },
        parent: { type: Array, required: true },
        collapsed: { type: Boolean, default: false },
        toggleCollapse: { type: Function, default: () => {} },
        level: { type: Number, required: true },
        treeLevels: { type: Array, required: true },
        readonly: { type: Boolean, default: false },
        displayOnlySelected: { type: Boolean, default: false },
        editMode: { type: Boolean, default: false },
        disableCreateAndEdit: { type: Boolean, default: false },
        background: { type: String, default: '' },
        customWidth: { type: String, default: '95%' },
        hasMatchingChildren: { type: Boolean, default: null },
        filteredChildCount: { type: Number, default: null },
        inputs: {
            type: Object,
            default: () => ({}),
            validator: (inputs) => {
                // Validate that inputs object has correct structure
                const validLevels = ['global', 'group', 'item'];
                return Object.keys(inputs).every((level) => validLevels.includes(level));
            }
        },
        getInputValue: {
            type: Function,
            default: () => null,
            required: false
        },
        handleInputChange: {
            type: Function,
            default: () => {},
            required: false
        }
    },
    inject: ['visibleIds', 'sizeMap', 'computeSize', 'editingMap', 'importCsvFile'],
    computed: {
        hasChildren() {
            // Group nodes should show chevron if they have children
            if (this.node.isGroupNode) {
                return this.node.children?.length > 0;
            }

            // During search, use hasMatchingChildren if provided
            if (this.hasMatchingChildren !== null) {
                return this.hasMatchingChildren;
            }

            // Regular nodes
            return this.node.children?.length > 0;
        },
        isLast() {
            return this.parent.indexOf(this.node) === this.parent.length - 1;
        },
        isNodeChecked() {
            return this.hasChildren ? this.node.children.some((c) => c.isSelected) : this.node.isSelected;
        },
        areChildrenPartiallySelected() {
            const tot = this.node.children?.length || 0;
            const sel = this.node.children?.filter((c) => c.isSelected).length || 0;
            return sel > 0 && sel < tot;
        },
        nextLevel() {
            // Group nodes don't have real schema levels, so they don't have a next level
            if (this.node.isGroupNode) {
                return null;
            }
            const idx = this.treeLevels.findIndex((l) => l.schemaLevel === this.node.schemaLevel);
            return this.treeLevels[idx + 1] || null;
        },
        childLabel() {
            if (!this.nextLevel) return '';
            const lvl = (this.nextLevel?.schemaLevel || 'item').toLowerCase();
            return this.node.children.length === 1 ? lvl : lvl + 's';
        },
        zIndexHeader() {
            return Math.max(200 - this.level * 10, 0);
        },
        zIndexToolbar() {
            return this.zIndexHeader + 1;
        },
        isError() {
            // don’t ever show errors when not in edit mode
            if (!this.editMode || this.disableCreateAndEdit) {
                return false;
            }

            const name = (this.node.name || '').trim();
            // empty?
            if (!name) return true;
            // wildcard?
            if (name.includes('*')) return true;
            // duplicate within this level?
            if (this.parent.some((other) => other !== this.node && (other.name || '').trim() === name)) {
                return true;
            }
            return false;
        },
        isEditing() {
            return this.editingMap[this.node.id];
        }
    },
    mounted() {
        this.debouncedDuplicate.bind(this);
    },
    methods: {
        startEdit() {
            this.editingMap[this.node.id] = true;
        },
        finishEdit() {
            if (!this.isError) {
                this.editingMap[this.node.id] = false;
                this.$emit('duplicate'); // to re-check errors
            }
        },
        toggleSelected(newValue) {
            // Handle tristate checkbox values: true, false, or 'indeterminate'
            // When indeterminate, we want to set to true (select all children)
            const isChecked = newValue === true || newValue === 'indeterminate';
            this.node.isSelected = isChecked;

            if (this.hasChildren) {
                const recurse = (arr, sel) => {
                    for (const c of arr) {
                        if (!c.disabled) {
                            c.isSelected = sel;
                        }
                        if (c.children) recurse(c.children, sel);
                    }
                };
                recurse(this.node.children, isChecked);
            }

            this.$emit('duplicate');
            this.$emit('select-toggled');
        },
        removeNode(parentArr, node) {
            const idx = parentArr.indexOf(node);
            if (idx !== -1) {
                parentArr.splice(idx, 1);
                this.$emit('duplicate');
            }
        },
        debouncedDuplicate: debounce(function () {
            this.$emit('duplicate');
        }, 300),
        addChild() {
            if (!this.nextLevel) return;
            const entry = {
                name: '',
                isSelected: true,
                collapsed: true,
                schemaLevel: this.nextLevel?.schemaLevel || 'item',
                schemaObject: this.nextLevel.schemaObject,
                children: [],
                placeholder: `Enter ${this.nextLevel?.schemaLevel || 'item'} name`
            };
            Object.defineProperty(entry, 'id', {
                value: crypto.randomUUID(),
                writable: false,
                enumerable: false
            });

            this.node.children = this.node.children || [];
            this.node.children.push(entry);
            // Size will be computed by TreeView's centralized size system
            this.editingMap[entry.id] = true;

            this.$emit('duplicate');
        },
        openFileUpload() {
            const fileInput = this.$refs.fileInput;
            if (fileInput) {
                fileInput.value = '';
                fileInput.click();
            }
        },
        onCsvChange(e) {
            const file = e.target.files[0];
            this.importCsvFile(file, {
                type: 'child-level',
                schemaLevel: this.nextLevel?.schemaLevel || 'item',
                schemaObject: this.nextLevel?.schemaObject,
                parentNode: this.node,
                onComplete: () => {
                    this.$emit('duplicate');
                }
            });
        }
    }
};
</script>

<style scoped>
@reference '../style.css';

/* Tree spacing constants - adjust these to modify the entire tree spacing */
:root {
    --tree-node-vertical-padding: 0.25rem; /* py-1 = 4px */
    --tree-node-bottom-spacing: 0.25rem; /* pb-1 = 4px */
    --tree-toolbar-top-margin: 4px;
    --tree-separator-bottom-padding: 1px;
    --tree-node-horizontal-padding: 0.5rem; /* px-2 = 8px */
    --tree-item-gap: 0.375rem; /* gap-1.5 = 6px */
}

.branch-horizontal {
    @apply w-5 h-px bg-border absolute left-0 top-1/2 -translate-y-px z-[-1];
}

.node-header {
    @apply relative pl-6;
}

.children-toolbar {
    @apply flex items-center gap-1.5 py-1.5 px-2 bg-inherit border-b border-border w-[calc(100%-2rem)];
    top: 100%;
    margin-top: var(--tree-toolbar-top-margin);
}

.children-toolbar-separator {
    @apply bg-inherit border-b border-border w-[calc(100%-2rem)];
    top: 100%;
    margin-top: var(--tree-toolbar-top-margin);
    padding-bottom: var(--tree-separator-bottom-padding);
    height: 1px;
}

.children-list {
    @apply border-b border-border w-[calc(100%-1.5rem)];
}

.tree-node-with-branch {
    @apply border-l border-border relative;
}

.tree-node-with-branch-last::after {
    @apply content-[''] absolute left-0 top-0 bottom-1/2 border-l border-border;
}

.sticky-node {
    @apply sticky top-0 bg-inherit;
    z-index: 10;
}

.node-container {
    @apply box-border relative overflow-visible;
}

.node-input {
    height: 35px !important;
}

/* Enhanced visual hierarchy for group nodes */
:deep(.group-inputs-toolbox) {
    position: relative;
}

:deep(.group-inputs-toolbox)::before {
    content: '';
    position: absolute;
    left: -1px;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(to bottom, hsl(var(--primary) / 0.2) 0%, hsl(var(--primary) / 0.1) 100%);
    border-radius: 0 2px 2px 0;
}
</style>
